import React, { useEffect, useState, useCallback, Fragment, useRef } from "react";
import { InfoBox, Separator, Carousel } from "..";
import { WorkHistoryContainer, WorkPieceContainer, AlbumImage } from "./work-history-styles";
import axios from "axios";
import qs from "qs";
import spotifyFill from "@iconify/icons-akar-icons/spotify-fill";
import { useWorkHistory } from "../../hooks";
import uuid from "react-uuid";
import { MOBILE_WIDTH } from "../../constants";
import { isBrowser } from "../../utils";
import Skeleton from "../skeleton/Skeleton";

export const WorkHistory = () => {
  const isBrowserSide = isBrowser();
  const [allRecords, setAllRecords] = useState([]);
  const [allCarouselItems, setAllCarouselItems] = useState([{}]);
  const allWorkHistory = useWorkHistory();
  const isMobileView = isBrowserSide && window.matchMedia(MOBILE_WIDTH).matches;
  const isMaxImageRes = isBrowserSide && window.matchMedia("(max-width: 600px)").matches;
  const isSmallIpadView = isBrowserSide && window.matchMedia("(max-width: 1250px)").matches;
  const isIpadView = isBrowserSide && window.matchMedia("(max-width: 1400px)").matches;
  const [itemWidth, setItemWidth] = useState(null);
  const workPieceRef = useRef(null);

  const getSpotifyDataForMultipleAlbums = useCallback(async () => {
    const encodedString = Buffer.from(
      `${process.env.GATSBY_SPOTIFY_CLIENT_ID}:${process.env.GATSBY_SPOTIFY_CLIENT_SECRET}`
    ).toString("base64");
    const data = {
      grant_type: "client_credentials",
    };
    const headers = {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Basic ${encodedString}`,
      },
    };
    try {
      const response = await axios.post("https://accounts.spotify.com/api/token", qs.stringify(data), headers);
      if (response && response.data.access_token && allWorkHistory?.length !== 0) {
        const authToken = response.data.access_token;
        const queryString = allWorkHistory.links.map(({ albumId }) => {
          return albumId;
        });
        const joinedQueryString = queryString.join(",");
        const albumResponse = await axios.get(`https://api.spotify.com/v1/albums?ids=${joinedQueryString}`, {
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + authToken,
            "Content-Type": "application/json",
          },
        });
        if (albumResponse && albumResponse.data) {
          const res = albumResponse.data.albums.reduce((acc, { artists, images, name, external_urls, id }) => {
            const obj = {
              artist: artists[0].name,
              image: images[0].url,
              name,
              url: external_urls.spotify,
              id,
            };
            allWorkHistory.links.forEach(({ albumId, ...album }) => {
              if (albumId === id) {
                const newObj = { ...obj, ...album };
                acc.push(newObj);
              }
            });
            return acc;
          }, []);
          setAllRecords([...res]);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }, [allWorkHistory.links, allWorkHistory.length]);

  useEffect(() => {
    getSpotifyDataForMultipleAlbums();
  }, [getSpotifyDataForMultipleAlbums]);

  useEffect(() => {
    if (allRecords?.length !== 0) {
      const carouselItems = allRecords.map(({ artist, image, name, url, recorded, mixed, mastered }) => {
        const title = [];
        if (recorded) title.push("Recorded");
        if (mixed) title.push("Mixed");
        if (mastered) title.push("Mastered");
        const subtitle = `${artist} - ${name}`;
        return {
          id: url,
          renderItem: (
            <Fragment>
              <WorkPieceContainer key={uuid()} ref={workPieceRef} $width={`${itemWidth}px`}>
                <InfoBox
                  title={title.join(", ")}
                  subtitle={subtitle}
                  subtitleUrl={url}
                  links={[
                    {
                      url: url,
                      icon: spotifyFill,
                      size: "21.35px",
                    },
                  ]}
                />
                <Separator />
                <AlbumImage src={image} $width={`${itemWidth}px`} />
              </WorkPieceContainer>
              <Separator vertical />
            </Fragment>
          ),
        };
      });
      setAllCarouselItems(carouselItems);
    } else {
      const arr = new Array(5).fill({
        id: uuid(),
        renderItem: (
          <Fragment>
            <WorkPieceContainer key={uuid()} ref={workPieceRef} $width={`${itemWidth}px`}>
              <InfoBox />
              <Separator />
              <Skeleton>
                <rect x="0" y="0" rx="2" ry="2" width={itemWidth} height={itemWidth} />
              </Skeleton>
            </WorkPieceContainer>
            <Separator vertical />
          </Fragment>
        ),
      });
      setAllCarouselItems(arr);
    }
  }, [allRecords, itemWidth]);

  useEffect(() => {
    if (workPieceRef?.current?.offsetWidth) {
      setItemWidth(workPieceRef?.current?.offsetWidth);
    }
    const updateWidth = () => {
      if (workPieceRef?.current?.offsetWidth) {
        setItemWidth(workPieceRef?.current?.offsetWidth);
      }
    };
    window.addEventListener("resize", updateWidth);
    return () => {
      window.removeEventListener("resize", updateWidth);
    };
  }, [workPieceRef, allCarouselItems, allWorkHistory, isMobileView, isMaxImageRes, isSmallIpadView, isIpadView]);

  const calcItemsPerSlide = () => {
    if (isMaxImageRes && allCarouselItems.length >= 1) {
      return 1;
    }
    if (isMobileView && allCarouselItems.length >= 2) {
      return 2;
    }
    if (isSmallIpadView && allCarouselItems.length >= 3) {
      return 3;
    }
    if (isIpadView && allCarouselItems.length >= 4) {
      return 4;
    }
    if (allCarouselItems.length >= 5) {
      return 5;
    } else {
      return allCarouselItems.length;
    }
  };

  return (
    <WorkHistoryContainer>
      {allCarouselItems.length >= 1 && (
        <Carousel carouselItems={allCarouselItems} itemsPerSlide={calcItemsPerSlide()} />
      )}
    </WorkHistoryContainer>
  );
};
